// Dock preference — whether the right panel rests OPEN (and on which slot) or
// CLOSED, persisted per device (pre-sync).
//
// J19 (owner call 2026-09-30): a deliberate CLOSE must stick across reloads
// rather than re-opening every load. Redesign step 1 (2026-10-19): the chat
// lives in this panel, so a fresh device starts OPEN on the Assistant. The key
// is new with the redesign: a close remembered while the panel held only the
// Context view must not hide the chat itself after the upgrade.
//
// Like the theme (theme.tsx) and the panel widths (panelLayout.ts), this is
// DEVICE display chrome, not a domain fact (memory
// project_theme_outside_event_log): it round-trips through its OWN localStorage
// key and is never emitted as a domain event. Only the impure shell
// reads/writes; the parse/serialize pair is pure and unit-tested.
//
// The default lives HERE (the device-preference layer), not in the pure
// `initialFrame` fold: the fold accepts whatever seed it is handed.
//
// Parse, don't validate: localStorage hands back `string | null` written by ANY
// earlier build. An absent key (fresh device) or an unknown value is the
// Assistant; the close sentinel is CLOSED; a known slot restores that slot.

import type {
    OpenSecondary,
    SecondarySlot,
} from "../components/AppShell/AppShell.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

const STORAGE_KEY = "bilda.rightPanel";

// The chat itself: where a fresh device and any unknown value land.
const ASSISTANT: SecondarySlot = "assistant";

// The sentinel a persisted CLOSE (an `OpenSecondary` of `null`) round-trips
// through. Distinct from an ABSENT key: absent = never chosen → the Assistant.
const CLOSED = "closed";

// Every slot, so a stored slot is re-established only if it is still a real one.
const SLOTS: readonly SecondarySlot[] = [
    "assistant",
    "review",
    "grounding",
    "panel",
];

const isSlot = (raw: string): raw is SecondarySlot =>
    SLOTS.some((slot) => slot === raw);

// --- Parsing / serializing (pure) --------------------------------------------

export const parseDockPreference = (raw: string | null): OpenSecondary =>
    raw === CLOSED ? null : raw !== null && isSlot(raw) ? raw : ASSISTANT;

export const serializeDockPreference = (pref: OpenSecondary): string =>
    pref === null ? CLOSED : pref;

// --- The impure seam (host-only, like theme.tsx / panelLayout.ts) ------------

export const readDockPreference = (): OpenSecondary =>
    parseDockPreference(readDeviceKey(STORAGE_KEY));

export const writeDockPreference = (pref: OpenSecondary): void =>
    writeDeviceKey(STORAGE_KEY, serializeDockPreference(pref));
